class SelectionModel {
    constructor(opts = {}) {
        this.selected = new Set();
        this.anchor = null; // «опорный» элемент для Shift-диапазона
        this.focused = null; // «текущий» элемент (строка с курсором)
        this.listeners = [];
        this.mode = opts.mode ?? 'multiple';
        this.keepOnReload = opts.keepOnReload ?? true;
        this._isSelectable = opts.isSelectable ?? (() => true);
        this._maxSelected = opts.maxSelected ?? null;
    }
    onChange(cb) {
        this.listeners.push(cb);
        return () => {
            this.listeners = this.listeners.filter(x => x !== cb);
        };
    }
    /** Полностью заменить выбор */
    set(ids) {
        const next = new Set();
        for (const id of ids) {
            if (!this._isSelectable(id))
                continue;
            if (this._maxSelected != null && next.size >= this._maxSelected)
                break;
            next.add(id);
            if (this.mode === 'single')
                break;
        }
        const prev = this._clone(this.selected);
        this.selected = next;
        const last = this._last(next) ?? null;
        this.focused = last;
        this.anchor = last;
        this._emit(prev, this.selected);
    }
    clear() {
        if (this.selected.size === 0 && !this.focused && !this.anchor)
            return;
        const prev = this._clone(this.selected);
        this.selected.clear();
        this.focused = null;
        this.anchor = null;
        this._emit(prev, this.selected);
    }
    select(id) {
        if (!this._isSelectable(id))
            return;
        if (this.mode === 'single') {
            this.set([id]);
            return;
        }
        const prev = this._clone(this.selected);
        if (this._maxSelected == null || this.selected.size < this._maxSelected) {
            this.selected.add(id);
        }
        else {
            // если лимит — сдвигаем якорь на новый id,
            // (можно изменить поведение по желанию)
            this.selected.add(id);
        }
        this.focused = id;
        this.anchor = id;
        this._emit(prev, this.selected);
    }
    unselect(id) {
        if (!this.selected.has(id))
            return;
        const prev = this._clone(this.selected);
        this.selected.delete(id);
        if (this.focused === id)
            this.focused = this._last(this.selected) ?? null;
        if (this.anchor === id)
            this.anchor = this.focused;
        this._emit(prev, this.selected);
    }
    /**
     * Снять выбор у нескольких элементов.
     * @param ids список id для снятия выделения
     */
    unselectMany(ids) {
        let changed = false;
        const prev = this._clone(this.selected);
        for (const id of ids) {
            if (this.selected.has(id)) {
                this.selected.delete(id);
                if (this.focused === id)
                    this.focused = this._last(this.selected) ?? null;
                if (this.anchor === id)
                    this.anchor = this.focused;
                changed = true;
            }
        }
        if (changed) {
            this._emit(prev, this.selected);
        }
    }
    toggle(id) {
        if (this.selected.has(id))
            this.unselect(id);
        else
            this.select(id);
    }
    /**
     * Диапазон от anchor → toId по переданному порядку.
     * Если anchor отсутствует — используем focused, иначе первый выбранный, иначе toId.
     */
    range(toId, order) {
        if (!this._isSelectable(toId))
            return;
        if (this.mode === 'single') {
            this.set([toId]);
            return;
        }
        let start = this.anchor ?? this.focused ?? (this._first(this.selected) ?? toId);
        const i1 = order.indexOf(start);
        const i2 = order.indexOf(toId);
        if (i1 === -1 || i2 === -1) {
            // если один из элементов не найден — fallback в одиночный выбор
            this.set([toId]);
            return;
        }
        const [from, to] = i1 <= i2 ? [i1, i2] : [i2, i1];
        const prev = this._clone(this.selected);
        // в диапазоне держим только доступные и селектабельные
        const next = new Set();
        for (let i = from; i <= to; i++) {
            const id = order[i];
            if (this._isSelectable(id))
                next.add(id);
            if (this._maxSelected != null && next.size >= this._maxSelected)
                break;
        }
        this.selected = next;
        this.focused = toId;
        this.anchor = start;
        this._emit(prev, this.selected);
    }
    /**
     * После перерисовки данных «подчистить» выбор от отсутствующих элементов.
     */
    reconcile(availableIds) {
        const avail = new Set(availableIds);
        const prev = this._clone(this.selected);
        let changed = false;
        // удалить несуществующие
        for (const id of Array.from(this.selected)) {
            if (!avail.has(id)) {
                this.selected.delete(id);
                changed = true;
            }
        }
        if (this.focused && !avail.has(this.focused)) {
            this.focused = this._last(this.selected) ?? null;
            changed = true;
        }
        if (this.anchor && !avail.has(this.anchor)) {
            this.anchor = this.focused;
            changed = true;
        }
        if (changed)
            this._emit(prev, this.selected);
    }
    setFocused(id) {
        this.focused = id;
        if (id != null && this.mode === 'single')
            this.set([id]);
    }
    _emit(prev, next) {
        const added = new Set();
        const removed = new Set();
        for (const id of next)
            if (!prev.has(id))
                added.add(id);
        for (const id of prev)
            if (!next.has(id))
                removed.add(id);
        const payload = {
            selected: this._clone(next),
            anchor: this.anchor,
            focused: this.focused,
            diff: { added, removed }
        };
        for (const cb of this.listeners)
            cb(payload);
    }
    _clone(s) {
        return new Set(Array.from(s));
    }
    _first(s) {
        for (const v of s)
            return v;
        return undefined;
    }
    _last(s) {
        let last = undefined;
        for (const v of s)
            last = v;
        return last;
    }
}

export { SelectionModel };
